import { createSelector, createSlice } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'
import { jwtDecode, JwtPayload } from 'jwt-decode'

import type { RootState } from '../store'
import { getCustomService } from '../../helpers/ReduxHelpers'
import {
  ForgotPasswordMutation,
  ForgotPasswordMutationVariables,
  LoginMutation,
  LoginMutationVariables,
  MutationSocialLoginArgs,
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
  User,
  VerifyTwoFactorMutation,
  VerifyTwoFactorMutationVariables,
} from '../../graphql/generated/graphql'

import { actionTypes } from './types'

//
// Services
//

export const loginService = getCustomService<'login', LoginMutation, LoginMutationVariables>(
  'login'
)
export const socialLoginService = getCustomService<'socialLogin', User, MutationSocialLoginArgs>(
  'socialLogin'
)
export const forgotService = getCustomService<
  'forgot',
  ForgotPasswordMutation,
  ForgotPasswordMutationVariables
>('forgot')
export const resetService = getCustomService<
  'reset',
  ResetPasswordMutation,
  ResetPasswordMutationVariables
>('reset')
export const verifyTwoFactorService = getCustomService<
  'verifyTwoFactor',
  VerifyTwoFactorMutation,
  VerifyTwoFactorMutationVariables
>('verifyTwoFactor')

//
// Initial state
//

export type AuthState = {
  user: User | null
  token: string | null
  impersonate?: boolean
  isLoading: boolean
  // services
  login: typeof loginService.state
  socialLogin: typeof socialLoginService.state
  forgot: typeof forgotService.state
  reset: typeof resetService.state
  verifyTwoFactor: typeof verifyTwoFactorService.state
}

const initialState: AuthState = {
  user: null,
  token: null,
  impersonate: false,
  isLoading: false,
  // services
  login: loginService.state,
  socialLogin: socialLoginService.state,
  forgot: forgotService.state,
  reset: resetService.state,
  verifyTwoFactor: verifyTwoFactorService.state,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuth: () => initialState,
    setUser: (state, { payload }: actionTypes.setUser) => {
      state.user = payload
    },
    setIsLoading: (state, action: actionTypes.setIsLoading) => {
      state.isLoading = action.payload
    },
    setToken: (state, action: actionTypes.setToken) => {
      state.token = action.payload
    },
    setImpersonate: (state, action: actionTypes.setImpersonate) => {
      state.impersonate = action.payload
    },
    logout: () => undefined,
    loadUser: () => undefined,
    ...loginService.reducers,
    ...socialLoginService.reducers,
    ...forgotService.reducers,
    ...resetService.reducers,
    ...verifyTwoFactorService.reducers,
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      const { user = null } = action.payload?.auth || {}
      return {
        ...initialState,
        user,
      }
    })
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const user = (state: RootState) => root(state).user
const impersonate = (state: RootState) => root(state).impersonate
const token = (state: RootState) => root(state).token
const jwt = createSelector([token], (tokenString): JwtPayload | null => {
  return tokenString ? jwtDecode<JwtPayload>(tokenString) : null
})
const isConnected = createSelector([token], (t): boolean => {
  return !!t && t !== ''
})
const isExpired = createSelector([jwt], (j): boolean => {
  return !!j && !!j.exp && j.exp * 1000 <= new Date().getTime()
})
const login = (state: RootState) => root(state).login
const forgot = (state: RootState) => root(state).forgot
const reset = (state: RootState) => root(state).reset
const isLoading = (state: RootState) => root(state).isLoading
const verifyTwoFactor = (state: RootState) => root(state).verifyTwoFactor

export const selectors = {
  user,
  impersonate,
  token,
  jwt,
  isConnected,
  isExpired,
  login,
  forgot,
  reset,
  isLoading,
  verifyTwoFactor,
}
